import { useMarkAllNotificationsRead } from '@doneverse/data/hooks'
import { notificationsOptions, unreadCountOptions, workspaceDetailedOptions } from '@doneverse/data/queries'
import { notificationService } from '@doneverse/data/services'
import type { Notification } from '@doneverse/shared'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Bell, CheckCheck } from 'lucide-react'
import { useMemo, useState } from 'react'
import styles from './NotificationBell.module.css'
import doneverseIcon from '@/assets/brand/icon-blue-green.png'
import { DeviceNotificationsDialog } from '@/components/notifications/DeviceNotificationsDialog'
import { Button } from '@/components/ui/button'
import { CountBadge } from '@/components/ui/count-badge'
import { IndicatorBadge } from '@/components/ui/indicator-badge'
import { NotificationDot } from '@/components/ui/notification-dot'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { usePushSubscription } from '@/hooks/usePushSubscription'
import { isMobileBridge } from '@/lib/mobile-bridge'
import {
  renderFormattedBody,
  getNotificationImage,
  getNotificationIcon,
  getNotificationRoute,
  formatTimeAgo,
} from '@/lib/notification-utils'
import { APP_SHELL_ROLES } from '@/lib/routes'

/* Web-push enable CTA. Only mounted while the panel is open, so the push hook
   (service-worker registration, permission checks) doesn't run on every page
   load. Hidden once this device is subscribed and where enabling is impossible
   (unsupported browser, insecure context). */
function EnableDeviceNotificationsLink({ onOpen }: { onOpen: () => void }) {
  const push = usePushSubscription()

  if (push.status !== 'idle' && push.status !== 'denied') return null

  return (
    <Button variant="link" className="h-auto p-0 text-sm" onClick={onOpen}>
      Enable device notifications
    </Button>
  )
}

export function NotificationBell() {
  const { user, profile } = useAuth()
  // /notifications is an app-shell page; a role outside the shell would only be
  // redirected back to its home (ClickUp 14zf25znmg1).
  const canOpenNotificationsPage = !!profile && APP_SHELL_ROLES.includes(profile.role)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Lightweight count query — always active for the badge
  const { data: unreadData } = useQuery(unreadCountOptions())
  const unreadCount = unreadData?.count ?? 0

  // Full notifications + workspace data — only fetched when panel is open
  const { data: notifications = [], isLoading: notificationsLoading } = useQuery({
    ...notificationsOptions(),
    enabled: open,
  })
  const { data: workspaceData } = useQuery({ ...workspaceDetailedOptions(), enabled: open })

  // Build lookup maps from workspace data
  const { playbookThumbnailMap, playbookSlugMap } = useMemo(() => {
    const thumbnails = new Map<string, string | null>()
    const slugs = new Map<string, string>()
    for (const wp of workspaceData?.playbooks ?? []) {
      const url = wp.playbook.thumbnail_url ?? null
      thumbnails.set(wp.id, url)
      thumbnails.set(wp.playbook_id, url)
      if (wp.playbook.slug) {
        slugs.set(wp.id, wp.playbook.slug)
        slugs.set(wp.playbook_id, wp.playbook.slug)
      }
    }
    return { playbookThumbnailMap: thumbnails, playbookSlugMap: slugs }
  }, [workspaceData])

  const markAsReadMutation = useMutation({
    mutationFn: async (notification: Notification) => {
      await notificationService.markNotificationAsRead(notification.id)
      return notification
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsOptions().queryKey })
      queryClient.invalidateQueries({ queryKey: unreadCountOptions().queryKey })
    },
  })

  const markAllAsReadMutation = useMarkAllNotificationsRead()

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsReadMutation.mutate(notification)
    }
    const route = getNotificationRoute(notification, playbookSlugMap)
    if (route) {
      setOpen(false)
      navigate(route)
    }
  }

  if (!user) return null

  const hasContent = notifications.length > 0

  return (
    <>
      {/* Mounted on demand so the push hooks (service-worker registration,
          permission checks) only run when the user opens the settings. */}
      {settingsOpen && <DeviceNotificationsDialog open onOpenChange={setSettingsOpen} />}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button type="button" aria-label="Notifications" className={styles.bellButton}>
            <IndicatorBadge show={unreadCount > 0} indicator={<CountBadge count={unreadCount} />}>
              <Bell className="w-5 h-5 text-muted-foreground" />
            </IndicatorBadge>
          </button>
        </SheetTrigger>
        <SheetContent
          side="right"
          // Mobile (<lg/1024): centered modal with even insets + visible close button.
          // Desktop (lg+): pinned top-right popover, close button hidden.
          // This card is offset from the screen edge (top-[56px]/bottom-[10px]), so
          // it already clears the status bar — pin the close button back to the card
          // corner instead of the Sheet default's safe-area offset.
          className="p-0 flex flex-col !h-auto z-[1001] shadow-lg w-auto !left-[10px] !right-[10px] !top-[56px] !bottom-[10px] rounded-[14px] [&>button:last-of-type]:!top-4 [&>button:last-of-type]:!right-4 lg:w-[378px] lg:max-w-[378px] lg:!left-auto lg:!right-[18px] lg:!top-[52px] lg:!bottom-[18px] lg:rounded-[11px] lg:[&>button:last-of-type]:hidden"
          overlayClassName="bg-transparent"
        >
          <SheetHeader className={styles.panelHeader}>
            <div className={styles.headerRow}>
              <SheetTitle className={styles.panelTitle}>Notifications</SheetTitle>
              <div className={styles.headerActions}>
                {unreadCount > 0 && (
                  <Button
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => markAllAsReadMutation.mutate()}
                    disabled={markAllAsReadMutation.isPending}
                  >
                    <CheckCheck className="w-4 h-4 mr-1" />
                    Mark all read
                  </Button>
                )}
                {/* Meaningless inside the native app's WebView, where push is
                    delivered natively via device tokens. */}
                {!isMobileBridge() && (
                  <EnableDeviceNotificationsLink
                    onOpen={() => {
                      setOpen(false)
                      setSettingsOpen(true)
                    }}
                  />
                )}
              </div>
            </div>
            <SheetDescription className="sr-only">Your recent notifications</SheetDescription>
          </SheetHeader>

          <ScrollArea className={styles.scrollContainer}>
            {notificationsLoading ? (
              <div className={styles.notificationList}>
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className={styles.skeletonItem}>
                    <Skeleton className="w-[60px] h-[60px] rounded-full shrink-0" />
                    <div className={styles.skeletonContent}>
                      <Skeleton className="h-[14px] w-full" />
                      <Skeleton className="h-[14px] w-3/4" />
                      <Skeleton className="h-[14px] w-[36px] mt-[2px]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : hasContent ? (
              <div className={styles.notificationList}>
                {notifications.map((notification) => {
                  const image = getNotificationImage(notification, playbookThumbnailMap)
                  const Icon = getNotificationIcon(notification.type)
                  const isUnread = !notification.read_at

                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={styles.notificationItem}
                    >
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          className={styles.thumbnail}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : Icon ? (
                        <div className={styles.thumbnailSystem}>
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                      ) : (
                        <div className={styles.thumbnailSystem}>
                          <img src={doneverseIcon} alt="" />
                        </div>
                      )}
                      <div className={styles.contentArea}>
                        <p className={isUnread ? styles.bodyText : styles.bodyTextRead}>
                          {renderFormattedBody(notification.body || notification.title)}
                        </p>
                        <span className={isUnread ? styles.timestamp : styles.timestampRead}>
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      {isUnread && (
                        <NotificationDot size="md" className={styles.unreadDot} data-testid="unread-dot" />
                      )}
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Bell />
                <p>No notifications yet</p>
              </div>
            )}
          </ScrollArea>

          {canOpenNotificationsPage && (
            <button
              type="button"
              className={styles.showAll}
              onClick={() => {
                setOpen(false)
                navigate({ to: '/notifications' })
              }}
            >
              Show all notifications
            </button>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}
